import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import type { LucideProps } from 'lucide-react';
import {
  Activity,
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  AtSign,
  BadgeCheck,
  Ban,
  Banknote,
  BarChart3,
  BellRing,
  Bike,
  BookOpen,
  Building2,
  Calendar,
  CalendarCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleAlert,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  ClipboardX,
  Clock,
  Compass,
  Copy,
  CreditCard,
  Crown,
  Download,
  Eye,
  EyeOff,
  FileText,
  Folder,
  FolderOpen,
  FolderTree,
  Gavel,
  Gem,
  Globe,
  GraduationCap,
  Grid2X2,
  Handshake,
  Headset,
  History,
  Hourglass,
  IdCard,
  Image as ImageIcon,
  Info,
  Landmark,
  LayoutDashboard,
  LibraryBig,
  LifeBuoy,
  LineChart,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  ListTodo,
  Loader2,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Map,
  MapPin,
  MapPinOff,
  Medal,
  Megaphone,
  Menu,
  MessageCircleQuestion,
  MessageSquare,
  MousePointerClick,
  MoreVertical,
  NotebookPen,
  Package,
  PackageOpen,
  Pencil,
  Phone,
  PlayCircle,
  Plus,
  PlusCircle,
  QrCode,
  Receipt,
  RefreshCw,
  Rocket,
  Save,
  Search,
  Send,
  Share2,
  Settings2,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  SquarePen,
  Star,
  Store,
  Tag,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  Upload,
  User,
  UserCog,
  UserPlus,
  UserX,
  Users,
  Video,
  Volume2,
  Wallet,
  Wrench,
  X,
  XCircle,
  Zap,
} from 'lucide-react';

import { palette } from '../../constants/palette';

type IconComponent = ForwardRefExoticComponent<LucideProps & RefAttributes<SVGSVGElement>>;

const SYMBOL_MAP: Record<string, IconComponent> = {
  dashboard: LayoutDashboard,
  layout_dashboard: LayoutDashboard,
  arrow_back: ArrowLeft,
  group: Users,
  groups: Users,
  storefront: Store,
  two_wheeler: Bike,
  account_balance_wallet: Wallet,
  local_shipping: Truck,
  assignment: ClipboardList,
  notifications_active: BellRing,
  tune: SlidersHorizontal,
  card_membership: CreditCard,
  credit_card: CreditCard,
  campaign: Megaphone,
  sell: Tag,
  handshake: Handshake,
  folder_managed: FolderTree,
  gavel: Gavel,
  close: X,
  person: User,
  manage_accounts: UserCog,
  supervisor_account: UserCog,
  settings_suggest: Settings2,
  logout: LogOut,
  public: Globe,
  security: Shield,
  expand_more: ChevronDown,
  mail: Mail,
  phone: Phone,
  verified_user: BadgeCheck,
  widgets: Grid2X2,
  admin_panel_settings: Settings2,
  forward_to_inbox: Send,
  touch_app: MousePointerClick,
  radio_button_unchecked: Circle,
  lock: Lock,
  visibility: Eye,
  visibility_off: EyeOff,
  stars: Star,
  trending_up: TrendingUp,
  check_circle: CheckCircle2,
  download: Download,
  search: Search,
  filter_list: SlidersHorizontal,
  person_add: UserPlus,
  location_on: MapPin,
  edit_square: SquarePen,
  more_vert: MoreVertical,
  history: History,
  help: CircleHelp,
  help_outline: CircleHelp,
  help_center: MessageCircleQuestion,
  contact_support: Headset,
  life_buoy: LifeBuoy,
  support: Headset,
  support_agent: Headset,
  headset_mic: Headset,
  headset: Headset,
  block: Ban,
  payments: Banknote,
  alternate_email: AtSign,
  call: Phone,
  location_city: Building2,
  delete: Trash2,
  delete_forever: Trash2,
  sync: RefreshCw,
  account_balance: Landmark,
  list_alt: ListChecks,
  checklist: ListChecks,
  quiz: ClipboardCheck,
  event_busy: ClipboardX,
  pending: Hourglass,
  info: Info,
  hourglass_empty: Hourglass,
  schedule: Clock,
  check: Check,
  cancel: XCircle,
  assignment_late: ClipboardX,
  hail: PackageOpen,
  warning: CircleAlert,
  inventory_2: Package,
  receipt_long: Receipt,
  swap_vert: ArrowDownUp,
  location_off: MapPinOff,
  assignment_ind: IdCard,
  monitoring: Activity,
  bar_chart: BarChart3,
  verified: BadgeCheck,
  send: Send,
  share_2: Share2,
  copy: Copy,
  link: Link2,
  qr_code: QrCode,
  bolt: Zap,
  pending_actions: ListTodo,
  auto_awesome: Sparkles,
  workspace_premium: Crown,
  diamond: Gem,
  auto_graph: LineChart,
  edit: Pencil,
  upload: Upload,
  refresh: RefreshCw,
  map: Map,
  analytics: BarChart3,
  description: FileText,
  badge: BadgeCheck,
  military_tech: Medal,
  emoji_events: Medal,
  sort: ArrowUpDown,
  picture_as_pdf: FileText,
  phone_iphone: Smartphone,
  travel_explore: Compass,
  place: MapPin,
  timer: Timer,
  error: CircleAlert,
  menu: Menu,
  notifications: BellRing,
  school: GraduationCap,
  cast_for_education: GraduationCap,
  person_search: User,
  co_present: Users,
  work: Building2,
  calendar_month: Calendar,
  event: Calendar,
  event_available: CalendarCheck,
  build: Wrench,
  folder_special: Sparkles,
  login: LogIn,
  menu_book: BookOpen,
  play_circle: PlayCircle,
  video: Video,
  forum: MessageSquare,
  library_big: LibraryBig,
  edit_note: NotebookPen,
  list: List,
  format_list_numbered: ListOrdered,
  folder_shared: FolderOpen,
  folder: Folder,
  settings: Settings2,
  apps: Grid2X2,
  add: Plus,
  add_circle: PlusCircle,
  image: ImageIcon,
  shield: Shield,
  article: FileText,
  save: Save,
  rocket_launch: Rocket,
  volume_up: Volume2,
  person_off: UserX,
  star: Star,
  progress_activity: Loader2,
  arrow_forward: ArrowRight,
  arrow_right: ArrowRight,
  shopping_cart: ShoppingCart,
  store: Store,
};

export type LucideSymbolTone = 'primary' | 'muted' | 'inherit';

export interface LucideSymbolIconProps extends Omit<LucideProps, 'color' | 'fill'> {
  /** Nom de l’icône Material Symbols d’origine (ligature). */
  name: string;
  /** Couleur sémantique : `primary` / `muted` utilisent `palette` ; `inherit` suit `currentColor` (ex. sidebar, pastilles). */
  tone?: LucideSymbolTone;
  /** Remplissage partiel (ex. étoile « pleine »). */
  filled?: boolean;
}

const FALLBACK: IconComponent = CircleAlert as unknown as IconComponent;

export default function LucideSymbolIcon({
  name,
  size = 20,
  className,
  tone = 'inherit',
  filled = false,
  strokeWidth = 2,
  style,
  ...rest
}: LucideSymbolIconProps) {
  const Cmp = SYMBOL_MAP[name] ?? FALLBACK;
  const resolvedColor = tone === 'primary' ? palette.primary : tone === 'muted' ? palette.textMuted : undefined;
  const useCurrent = tone === 'inherit';
  const fillPaint = filled ? (useCurrent ? 'currentColor' : resolvedColor ?? 'currentColor') : 'none';

  return (
    <Cmp
      size={size}
      color={resolvedColor}
      strokeWidth={strokeWidth}
      fill={fillPaint}
      className={className}
      style={style}
      aria-hidden
      {...rest}
    />
  );
}
